import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  message,
  Table,
  Popconfirm,
  Typography,
  Modal,
  Input,
  Row,
  Col,
  DatePicker,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
  QrcodeOutlined,
  ExperimentOutlined,
  SearchOutlined,
  SyncOutlined,
  CopyOutlined,
  CalendarOutlined,
  DownOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Menu } from 'antd';
import weworkContactWayService from '../services/weworkContactWayService';
import weworkContactWayStatisticsService from '../services/weworkContactWayStatisticsService';
import weworkService from '../services/weworkService';
import tagService from '../services/tagService';
import dayjs from 'dayjs';
import './WeworkContactWay.css';

const { Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const WeworkContactWay = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contactWays, setContactWays] = useState([]);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [currentQrCode, setCurrentQrCode] = useState('');
  const [currentContactWayName, setCurrentContactWayName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDownloading, setBatchDownloading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  // 新增：时间范围选择和统计数据相关状态
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'), // 默认最近30天
    dayjs()
  ]);
  const [statisticsData, setStatisticsData] = useState({});
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [exportingDaily, setExportingDaily] = useState(false);
  const [exportingSummary, setExportingSummary] = useState(false);
  const [exportingConfigs, setExportingConfigs] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 导出体量上限（活码数 × 天数）。可根据后端性能调整。
  const MAX_DAILY_EXPORT_VOLUME = 10000;

  // 加载数据
  useEffect(() => {
    loadInitialData();
  }, []);

  // 当时间范围变化时，重新加载统计数据
  useEffect(() => {
    if (contactWays.length > 0) {
      loadStatisticsData();
    }
  }, [dateRange, contactWays]);

  // 加载初始数据
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 并行加载员工、标签数据
      const [employeesData, tagsData] = await Promise.all([
        weworkService.getAllEmployees().catch(err => {
          console.error('获取员工数据失败:', err);
          return [];
        }),
        tagService.getAllTags().catch(err => {
          console.error('获取标签数据失败:', err);
          return [];
        })
      ]);

      // 处理员工数据，确保正确格式
      let processedEmployees = [];
      if (Array.isArray(employeesData)) {
        processedEmployees = employeesData;
      } else if (employeesData && employeesData.content) {
        processedEmployees = employeesData.content;
      } else if (employeesData && Array.isArray(employeesData.data)) {
        processedEmployees = employeesData.data;
      }

      setEmployees(processedEmployees || []);
      setTags(tagsData || []);

      // 加载活码数据
      await loadContactWays();
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载活码列表
  const loadContactWays = async (page = 1, pageSize = 10, remarkKeyword = '') => {
    setLoading(true);
    try {
      // 确保参数是有效的数字
      const validPage = Number.isInteger(page) && page > 0 ? page : 1;
      const validPageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 10;

      const params = {
        page: validPage - 1, // 后端从0开始
        size: validPageSize
      };

      // 添加搜索参数
      if (remarkKeyword && remarkKeyword.trim()) {
        params.remark = remarkKeyword.trim();
      }

      console.log('📝 加载活码列表参数:', params);

      const data = await weworkContactWayService.getContactWays(params);
      if (data.success) {
        setContactWays(data.data || []);
        setPagination({
          current: validPage,
          pageSize: validPageSize,
          total: data.totalElements || 0
        });

        // 加载完活码列表后，立即加载统计数据
        if (data.data && data.data.length > 0) {
          loadStatisticsData(data.data);
        }
      } else {
        message.error(data.message || '加载活码列表失败');
      }
    } catch (error) {
      console.error('加载活码列表失败:', error);
      message.error('加载活码列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 新增：加载统计数据（优化版本，使用批量查询）
  const loadStatisticsData = async (contactWaysList = contactWays) => {
    if (!contactWaysList || contactWaysList.length === 0) {
      return;
    }

    setStatisticsLoading(true);
    try {
      const startDate = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;

      // 提取所有活码的configId
      const contactWayConfigIds = contactWaysList.map(contactWay => contactWay.configId);

      // 批量获取累计统计（不限时间范围）
      const totalStatsPromise = weworkContactWayStatisticsService.getBatchSummary({
        contactWayConfigIds: contactWayConfigIds,
        startDate: null, // 不限开始时间，获取累计数据
        endDate: null
      });

      // 批量获取时间范围内统计
      const rangeStatsPromise = weworkContactWayStatisticsService.getBatchSummary({
        contactWayConfigIds: contactWayConfigIds,
        startDate: startDate,
        endDate: endDate
      });

      const [totalStatsMap, rangeStatsMap] = await Promise.all([totalStatsPromise, rangeStatsPromise]);

      // 将结果转换为以活码ID为key的对象
      const statisticsMap = {};
      contactWaysList.forEach(contactWay => {
        const totalStats = totalStatsMap[contactWay.configId];
        const rangeStats = rangeStatsMap[contactWay.configId];

        statisticsMap[contactWay.id] = {
          totalAddCount: totalStats?.totalAddCount || 0,
          rangeAddCount: rangeStats?.totalAddCount || 0
        };
      });

      setStatisticsData(statisticsMap);
      console.log('✅ 批量统计数据加载完成:', statisticsMap);
    } catch (error) {
      console.error('加载统计数据失败:', error);
      message.error('加载统计数据失败');
    } finally {
      setStatisticsLoading(false);
    }
  };

  // 新增：处理时间范围变化
  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [dayjs().subtract(30, 'days'), dayjs()]);
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
    // 重置到第一页并执行搜索
    const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
    loadContactWays(1, safePageSize, value);
  };

  // 清除搜索
  const handleClearSearch = () => {
    setSearchKeyword('');
    // 重置到第一页并清除搜索
    const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
    loadContactWays(1, safePageSize, '');
  };

  // 处理分页变化
  const handleTableChange = (paginationInfo) => {
    const safeCurrent = Number.isInteger(paginationInfo.current) && paginationInfo.current > 0 ? paginationInfo.current : 1;
    const safePageSize = Number.isInteger(paginationInfo.pageSize) && paginationInfo.pageSize > 0 ? paginationInfo.pageSize : 10;
    loadContactWays(safeCurrent, safePageSize, searchKeyword);
  };

  // 下载Blob为文件
  const downloadBlobAsFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  };

  // 计算时间范围天数（含端点）
  const computeSelectedDays = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return 0;
    const start = dateRange[0].startOf('day');
    const end = dateRange[1].startOf('day');
    const diff = end.diff(start, 'day');
    return diff >= 0 ? diff + 1 : 0;
  };

  // // 拉取所有符合筛选条件（当前搜索关键字）的活码（全量，不限当前页）
  // const fetchAllFilteredContactWays = async () => {
  //   // 使用较大的分页一次拉多一些，减少请求次数
  //   const pageSize = 1000;
  //   let pageIndex = 0;
  //   let all = [];
  //   let totalElements = null;
  //   // 保护：最多抓取 100 页，避免异常循环
  //   const maxPages = 100;
  //   while (pageIndex < maxPages) {
  //     const params = {
  //       page: pageIndex,
  //       size: pageSize
  //     };
  //     if (searchKeyword && searchKeyword.trim()) {
  //       params.remark = searchKeyword.trim();
  //     }
  //     const data = await weworkContactWayService.getContactWays(params);
  //     if (!data || data.success === false) {
  //       break;
  //     }
  //     const list = Array.isArray(data.data) ? data.data : [];
  //     all = all.concat(list);
  //     if (typeof data.totalElements === 'number') {
  //       totalElements = data.totalElements;
  //     }
  //     if (list.length < pageSize) {
  //       break;
  //     }
  //     if (totalElements !== null && all.length >= totalElements) {
  //       break;
  //     }
  //     pageIndex += 1;
  //   }
  //   return all;
  // };

  // 导出按天明细
  const handleExportDaily = async () => {
    console.log('🔍 handleExportDaily 函数被调用');
    try {
      setExportingDaily(true);
      console.log('📊 状态已设置为导出中');
      // 优先使用选中的活码；若未选中则提示，不再默认导出所有
      const selected = contactWays.filter(item => selectedRowKeys.includes(item.id));
      if (!selected || selected.length === 0) {
        message.warning('请先在列表中勾选要导出的活码');
        message.warning('没有符合条件的活码可导出');
        return;
      }

      // 计算体量
      const days = computeSelectedDays();
      const volume = selected.length * days;
      if (volume > MAX_DAILY_EXPORT_VOLUME) {
        message.error(`导出体量过大（活码数×天数 = ${volume}），请缩小筛选范围。上限：${MAX_DAILY_EXPORT_VOLUME}`);
        return;
      }

      const startDate = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;
      const contactWayConfigIds = selected.map(item => item.configId).filter(Boolean);

      if (!startDate || !endDate) {
        message.warning('请选择有效的时间范围');
        return;
      }

      const blob = await weworkContactWayStatisticsService.exportDaily({
        contactWayConfigIds,
        startDate,
        endDate
      });
      const filename = `活码明细数据_${dayjs(startDate).format('YYYYMMDD')}-${dayjs(endDate).format('YYYYMMDD')}.xlsx`;
      downloadBlobAsFile(blob, filename);
      message.success('按天明细导出成功');
    } catch (error) {
      console.error('按天明细导出失败:', error);
      message.error('按天明细导出失败');
    } finally {
      setExportingDaily(false);
    }
  };

  // 导出时段汇总
  const handleExportSummary = async () => {
    try {
      setExportingSummary(true);
      // 优先使用选中的活码；若未选中则提示，不再默认导出所有
      const selected = contactWays.filter(item => selectedRowKeys.includes(item.id));
      if (!selected || selected.length === 0) {
        message.warning('请先在列表中勾选要导出的活码');
        message.warning('没有符合条件的活码可导出');
        return;
      }

      const startDate = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const endDate = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;
      const contactWayConfigIds = selected.map(item => item.configId).filter(Boolean);

      if (!startDate || !endDate) {
        message.warning('请选择有效的时间范围');
        return;
      }

      const blob = await weworkContactWayStatisticsService.exportSummary({
        contactWayConfigIds,
        startDate,
        endDate
      });
      const filename = `活码汇总数据_${dayjs(startDate).format('YYYYMMDD')}-${dayjs(endDate).format('YYYYMMDD')}.xlsx`;
      downloadBlobAsFile(blob, filename);
      message.success('时段汇总导出成功');
    } catch (error) {
      console.error('时段汇总导出失败:', error);
      message.error('时段汇总导出失败');
    } finally {
      setExportingSummary(false);
    }
  };

  // 导出选中活码配置
  const handleExportConfigs = async () => {
    try {
      setExportingConfigs(true);
      const selected = contactWays.filter(item => selectedRowKeys.includes(item.id));
      if (!selected || selected.length === 0) {
        message.warning('请先在列表中勾选要导出的活码');
        return;
      }
      const ids = selected.map(it => it.id);
      const blob = await weworkContactWayService.exportConfigs({ contactWayIds: ids });
      const filename = `活码配置数据_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      downloadBlobAsFile(blob, filename);
      message.success('活码配置导出成功');
    } catch (error) {
      console.error('活码配置导出失败:', error);
      message.error('活码配置导出失败');
    } finally {
      setExportingConfigs(false);
    }
  };

  const statsMenu = (
    <Menu
      onClick={({ key }) => {
        console.log('📋 Menu onClick triggered, key:', key);
        if (key === 'daily') {
          handleExportDaily();
        } else if (key === 'summary') {
          handleExportSummary();
        }
      }}
      items={[
        {
          key: 'daily',
          label: `按天${exportingDaily ? '（处理中）' : ''}`
        },
        {
          key: 'summary',
          label: `按时段${exportingSummary ? '（处理中）' : ''}`
        }
      ]}
    />
  );

  // 删除活码
  const handleDelete = async (id) => {
    try {
      const data = await weworkContactWayService.deleteContactWay(id);
      if (data.success) {
        message.success('删除成功');
        // 删除后重新加载，使用安全的分页参数
        const safeCurrent = Number.isInteger(pagination.current) && pagination.current > 0 ? pagination.current : 1;
        const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
        loadContactWays(safeCurrent, safePageSize, searchKeyword);
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 复制活码
  const handleCopyContactWay = async (record) => {
    try {
      console.log('准备复制活码:', record);

      // 显示输入框让用户编辑新活码名称
      Modal.confirm({
        title: '复制活码',
        content: (
          <div style={{ marginTop: 16 }}>
            <p>将复制活码「{record.remark || '未命名活码'}」的所有配置</p>
            <Input
              placeholder="请输入新活码名称"
              defaultValue={`${record.remark || '未命名活码'}_复制`}
              id="copy-remark-input"
              maxLength={30}
              showCount
            />
          </div>
        ),
        okText: '确认复制',
        cancelText: '取消',
        width: 480,
        onOk: async () => {
          const remarkInput = document.getElementById('copy-remark-input');
          const newRemark = remarkInput?.value?.trim();

          if (!newRemark) {
            message.error('请输入新活码名称');
            return Promise.reject();
          }

          try {
            message.loading('正在复制活码...', 0);

            const copyRequest = {
              remark: newRemark
            };

            const data = await weworkContactWayService.copyContactWay(record.id, copyRequest);

            message.destroy(); // 清除loading消息

            if (data.success) {
              message.success(`活码复制成功：${newRemark}`);
              // 复制成功后重新加载列表
              const safeCurrent = Number.isInteger(pagination.current) && pagination.current > 0 ? pagination.current : 1;
              const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
              await loadContactWays(safeCurrent, safePageSize, searchKeyword);
            } else {
              message.error(data.message || '复制失败');
            }
          } catch (error) {
            message.destroy(); // 清除loading消息
            console.error('复制活码失败:', error);
            const errorMsg = error.response?.data?.message || error.message || '复制失败';
            message.error('复制失败: ' + errorMsg);
            return Promise.reject();
          }
        }
      });
    } catch (error) {
      console.error('复制活码操作失败:', error);
      message.error('操作失败');
    }
  };

  // 预览二维码
  const handlePreviewQrCode = (record) => {
    if (!record.qrCode) {
      message.warning('该活码暂无二维码');
      return;
    }
    setCurrentQrCode(record.qrCode);
    setCurrentContactWayName(record.remark || '活码');
    setQrCodeModalVisible(true);
  };

  // 下载二维码
  const handleDownloadQrCode = async (record) => {
    if (!record.qrCode) {
      message.warning('该活码暂无二维码');
      return;
    }

    const fileName = `${record.remark || '活码'}_二维码.png`;

    try {
      // 尝试使用fetch下载（带上必要的headers）
      const response = await fetch(record.qrCode, {
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // 检查blob是否有效（大小大于0）
      if (blob.size === 0) {
        throw new Error('获取到的图片数据为空');
      }

      const blobUrl = window.URL.createObjectURL(blob);

      // 创建下载链接
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 清理blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

      message.success('二维码下载成功');
    } catch (error) {
      console.warn('直接下载失败，尝试备用方案:', error.message);

      // 备用方案1：尝试通过后端代理下载
      try {
        message.loading('正在准备下载...', 0);

        // 可以调用后端API来代理下载图片
        // const proxyResponse = await api.post('/wework/contact-way/download-qrcode', {
        //   qrCodeUrl: record.qrCode,
        //   fileName: fileName
        // });

        // 备用方案2：使用Canvas转换
        await downloadViaCanvas(record.qrCode, fileName);

      } catch (fallbackError) {
        message.destroy();
        console.error('所有下载方案都失败:', fallbackError);

        // 最后的备用方案：提示用户手动下载
        Modal.info({
          title: '下载提示',
          content: (
            <div>
              <p>由于网络限制，无法自动下载二维码。</p>
              <p>请点击下方链接，在新页面中右键保存图片：</p>
              <a
                href={record.qrCode}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff' }}
              >
                点击查看二维码
              </a>
            </div>
          ),
          okText: '我知道了'
        });
      }
    }
  };

  // Canvas备用下载方案
  const downloadViaCanvas = (imageUrl, fileName) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;

          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = fileName;
              link.style.display = 'none';

              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

              message.destroy();
              message.success('二维码下载成功');
              resolve();
            } else {
              reject(new Error('Canvas转换失败'));
            }
          }, 'image/png');
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      // 设置图片src，触发加载
      img.src = imageUrl;
    });
  };

  // 批量编辑
  const handleBatchEdit = () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.warning('请先在列表中勾选要批量编辑的活码');
      return;
    }

    const selectedItems = contactWays.filter(item => selectedRowKeys.includes(item.id));

    navigate('/wework-contact-way/batch-edit', {
      state: {
        selectedIds: [...selectedRowKeys],
        // 仅携带必要的展示字段，避免state过大
        selectedItems: selectedItems.map(it => ({
          id: it.id,
          remark: it.remark,
          status: it.status,
          userIds: it.userIds,
          createdAt: it.createdAt
        }))
      }
    });
  };

  // 手动同步活码
  const handleSyncContactWays = async () => {
    setSyncLoading(true);
    try {
      message.loading('正在从企业微信同步活码数据...', 0);

      const data = await weworkContactWayService.syncContactWaysFromWework();

      message.destroy(); // 清除loading消息

      if (data.success) {
        message.success(`同步完成，处理了 ${data.syncCount || 0} 个活码`);
        // 同步完成后重新加载列表，使用安全的分页参数
        const safeCurrent = Number.isInteger(pagination.current) && pagination.current > 0 ? pagination.current : 1;
        const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
        await loadContactWays(safeCurrent, safePageSize, searchKeyword);
      } else {
        message.error(data.message || '同步失败');
      }
    } catch (error) {
      message.destroy(); // 清除loading消息
      console.error('同步活码失败:', error);
      message.error('同步失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncLoading(false);
    }
  };

  // 批量下载二维码
  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要下载的活码');
      return;
    }

    setBatchDownloading(true);
    try {
      // 获取选中的活码数据
      const selectedContactWays = contactWays.filter(item =>
        selectedRowKeys.includes(item.id)
      );

      // 过滤出有二维码的活码
      const validContactWays = selectedContactWays.filter(item => item.qrCode);

      if (validContactWays.length === 0) {
        message.warning('选中的活码都没有二维码');
        return;
      }

      if (validContactWays.length < selectedContactWays.length) {
        message.info(`${selectedContactWays.length}个活码中有${validContactWays.length}个有二维码，将下载这${validContactWays.length}个`);
      }

      // 逐个下载二维码
      let successCount = 0;
      for (const contactWay of validContactWays) {
        try {
          await downloadSingleQrCode(contactWay);
          successCount++;

          // 添加延迟，避免浏览器阻止多个下载
          if (successCount < validContactWays.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`下载 ${contactWay.remark || '活码'} 二维码失败:`, error);
        }
      }

      message.success(`成功下载 ${successCount} 个二维码`);

      // 清除选择
      setSelectedRowKeys([]);

    } catch (error) {
      console.error('批量下载失败:', error);
      message.error('批量下载失败');
    } finally {
      setBatchDownloading(false);
    }
  };

  // 单个下载辅助函数
  const downloadSingleQrCode = async (contactWay) => {
    const fileName = `${contactWay.remark || '活码'}_二维码.png`;

    try {
      // 尝试使用fetch下载（带上必要的headers）
      const response = await fetch(contactWay.qrCode, {
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // 检查blob是否有效
      if (blob.size === 0) {
        throw new Error('获取到的图片数据为空');
      }

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 清理blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

    } catch (error) {
      // 批量下载时使用Canvas备用方案
      try {
        await downloadViaCanvas(contactWay.qrCode, fileName);
      } catch (canvasError) {
        console.error(`下载 ${contactWay.remark || '活码'} 失败:`, canvasError);
        throw new Error(`${contactWay.remark || '活码'} 下载失败`);
      }
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '活码名称',
      dataIndex: 'remark',
      key: 'remark',
      render: (text) => text || '未命名活码',
      width: 200
    },
    {
      title: '使用成员',
      dataIndex: 'userIds',
      key: 'users',
      width: 120,
      render: (userIds, record) => {
        if (!userIds) return <span style={{ color: '#999' }}>未配置成员</span>;

        let parsedUserIds = [];

        // 处理不同的数据格式
        if (Array.isArray(userIds)) {
          parsedUserIds = userIds;
        } else if (typeof userIds === 'string') {
          try {
            parsedUserIds = JSON.parse(userIds);
          } catch (error) {
            console.warn('解析用户ID列表失败:', userIds, error);
            return <span style={{ color: '#f5222d' }}>数据格式错误</span>;
          }
        }

        if (!Array.isArray(parsedUserIds) || parsedUserIds.length === 0) {
          return <span style={{ color: '#999' }}>未配置成员</span>;
        }

        // 根据userIds查找对应的员工名称
        // 注意：后端存储的userIds是企微userid，需要通过userid字段匹配
        const displayUsers = parsedUserIds.map((userId) => {
          // 优先按企微userid匹配（后端存储的格式）
          const employee = employees.find(emp => {
            return emp.userid === userId || emp.userid === String(userId);
          });

          if (employee) {
            return employee.name || userId;
          } else {
            // 如果按企微userid没找到，尝试按系统ID匹配（兼容旧数据）
            const empBySystemId = employees.find(emp => {
              return emp.id === userId || emp.id === Number(userId) || String(emp.id) === String(userId);
            });
            if (empBySystemId) {
              return empBySystemId.name || userId;
            }
            // 都没找到，显示原始ID
            return `${userId}(未匹配)`;
          }
        });

        return (
          <div>
            {displayUsers.length <= 3 ? (
              displayUsers.map((user, index) => (
                <div key={index} style={{ fontSize: '12px', color: '#1890ff' }}>
                  {user}
                </div>
              ))
            ) : (
              <>
                {displayUsers.slice(0, 2).map((user, index) => (
                  <div key={index} style={{ fontSize: '12px', color: '#1890ff' }}>
                    {user}
                  </div>
                ))}
                <div style={{ fontSize: '12px', color: '#999' }}>
                  +{displayUsers.length - 2}位成员
                </div>
              </>
            )}
          </div>
        );
      }
    },
    {
      title: '自动确认',
      dataIndex: 'skipVerify',
      key: 'skipVerify',
      width: 120,
      render: (skipVerify) => (
        <span style={{
          color: skipVerify ? '#52c41a' : '#faad14',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {skipVerify ? '✓ 是' : '✗ 否'}
        </span>
      )
    },
    // 新增：累计添加人数列
    {
      title: '累计添加',
      dataIndex: 'totalAddCount',
      key: 'totalAddCount',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const stats = statisticsData[record.id];
        if (statisticsLoading) {
          return <span style={{ color: '#999' }}>加载中...</span>;
        }
        return (
          <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
            {stats ? stats.totalAddCount : 0}
          </span>
        );
      }
    },
    // 新增：时间范围内添加人数列
    {
      title: '期间添加',
      dataIndex: 'rangeAddCount',
      key: 'rangeAddCount',
      width: 120,
      align: 'center',
      sorter: (a, b) => {
        const countA = statisticsData[a.id]?.rangeAddCount || 0;
        const countB = statisticsData[b.id]?.rangeAddCount || 0;
        return countA - countB;
      },
      sortDirections: ['descend', 'ascend'],
      render: (_, record) => {
        const stats = statisticsData[record.id];
        if (statisticsLoading) {
          return <span style={{ color: '#999' }}>加载中...</span>;
        }
        return (
          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
            {stats ? stats.rangeAddCount : 0}
          </span>
        );
      }
    },
    {
      title: '自动添加标签',
      dataIndex: 'autoTagIds',
      key: 'tags',
      width: 220,
      render: (autoTagIds, record) => {
        // 处理自动标签ID数据
        let tagIds = [];

        if (autoTagIds) {
          if (Array.isArray(autoTagIds)) {
            tagIds = autoTagIds;
          } else if (typeof autoTagIds === 'string') {
            try {
              tagIds = JSON.parse(autoTagIds);
            } catch (error) {
              // 如果解析失败，尝试按逗号分割
              tagIds = autoTagIds.split(',').filter(id => id.trim());
            }
          }
        }

        if (!Array.isArray(tagIds) || tagIds.length === 0) {
          return <span style={{ color: '#999', fontSize: '12px' }}>无标签</span>;
        }

        // 根据标签ID查找对应的标签名称
        const displayTags = tagIds.map((tagId) => {
          const tag = tags.find(t => t.id === Number(tagId) || t.id === tagId || t.sourceId === tagId);
          return tag ? tag.tagName : `标签${tagId}`;
        });

        return (
          <div>
            {displayTags.length <= 3 ? (
              displayTags.map((tagName, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    background: '#f0f0f0',
                    padding: '2px 6px',
                    margin: '1px 2px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    color: '#666'
                  }}
                >
                  {tagName}
                </span>
              ))
            ) : (
              <>
                {displayTags.slice(0, 2).map((tagName, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-block',
                      background: '#f0f0f0',
                      padding: '2px 6px',
                      margin: '1px 2px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      color: '#666'
                    }}
                  >
                    {tagName}
                  </span>
                ))}
                <span style={{ fontSize: '11px', color: '#999' }}>
                  +{displayTags.length - 2}个
                </span>
              </>
            )}
          </div>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (time) => time ? new Date(time).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => handlePreviewQrCode(record)}
            disabled={!record.qrCode}
            className="qr-preview-btn"
            title={record.qrCode ? "预览二维码" : "暂无二维码"}
          >
            预览
          </Button>
          <Button
            type="link"
            onClick={() => handleDownloadQrCode(record)}
            disabled={!record.qrCode}
            className="qr-download-btn"
            title={record.qrCode ? "下载二维码" : "暂无二维码"}
          >
            下载
          </Button>
          <Button
            type="link"
            onClick={() => handleCopyContactWay(record)}
            className="copy-btn"
            title="复制活码配置"
          >
            复制
          </Button>
          <Button
            type="link"
            onClick={() => navigate(`/wework-contact-way/edit/${record.id}`)}
            className="edit-btn"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个活码吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              className="delete-btn"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="wework-contact-way-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>成员活码</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <span style={{ color: '#666' }}>统计时间范围：</span>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  allowClear={false}
                  disabledDate={() => false}
                  placeholder={['开始日期', '结束日期']}
                />
                {searchKeyword && (
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    搜索"{searchKeyword}"，共找到 {pagination.total} 条结果
                    <Button
                      type="link"
                      size="small"
                      onClick={handleClearSearch}
                      style={{ padding: '0 4px', fontSize: '12px' }}
                    >
                      清除搜索
                    </Button>
                  </div>
                )}
                <Input.Search
                  placeholder="搜索活码名称"
                  allowClear
                  enterButton
                  onSearch={handleSearch}
                  style={{ width: 250 }}
                />
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/wework-contact-way/create')}
                >
                  创建活码
                </Button>
                <Button
                  type="primary"
                  icon={<ExperimentOutlined />}
                  onClick={handleBatchEdit}
                  disabled={selectedRowKeys.length === 0}
                >
                  批量编辑{selectedRowKeys.length > 0 ? `（${selectedRowKeys.length}）` : ''}
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleBatchDownload}
                  loading={batchDownloading}
                  disabled={selectedRowKeys.length === 0}
                >
                  批量下载二维码 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
                </Button>
                <Button
                  onClick={handleExportConfigs}
                  loading={exportingConfigs}
                  disabled={selectedRowKeys.length === 0}
                >
                  批量导出配置明细
                </Button>
                <Space size={4}>
                  <Dropdown overlay={statsMenu} trigger={["click"]} placement="bottomLeft" disabled={selectedRowKeys.length === 0}>
                    <Button disabled={selectedRowKeys.length === 0}>
                      导出活码人数统计 <DownOutlined />
                    </Button>
                  </Dropdown>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '12px' }}>
                        <div><strong>导出限制说明：</strong></div>
                        <div style={{ marginTop: '8px' }}>• 按天导出：活码数 × 天数 ≤ 10,000</div>
                        <div style={{ marginTop: '4px' }}>• 按时段导出：无特殊限制</div>
                        <div style={{ marginTop: '8px', color: '#faad14' }}>提示：如提示体量过大，请减少活码数量或缩短时间范围</div>
                      </div>
                    }
                    placement="topRight"
                  >
                    <QuestionCircleOutlined
                      style={{
                        fontSize: '16px',
                        color: '#8c8c8c',
                        cursor: 'pointer',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))'
                      }}
                    />
                  </Tooltip>
                </Space>
                {/* <Button
                  icon={<SyncOutlined />}
                  onClick={handleSyncContactWays}
                  loading={syncLoading}
                  title="从企业微信同步最新活码数据"
                >
                  同步活码
                </Button> */}
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          className="contact-way-table"
          columns={columns}
          dataSource={contactWays}
          rowKey="id"
          loading={loading || statisticsLoading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys, selectedRows) => {
              setSelectedRowKeys(selectedKeys);
            },
            getCheckboxProps: (record) => ({
              // 只有有二维码的活码才能被选中用于下载
              disabled: !record.qrCode,
              name: record.remark || '活码',
            }),
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `共 ${total} 条记录，当前显示 ${range[0]}-${range[1]} 条`,
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
            onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size })
          }}
        />
      </Card>

      {/* 二维码预览Modal */}
      <Modal
        title={`${currentContactWayName} - 活码二维码`}
        open={qrCodeModalVisible}
        onCancel={() => setQrCodeModalVisible(false)}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => {
            const record = { qrCode: currentQrCode, remark: currentContactWayName };
            handleDownloadQrCode(record);
          }}>
            下载二维码
          </Button>,
          <Button key="close" onClick={() => setQrCodeModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={400}
        centered
        className="qr-code-modal"
      >
        <div className="qr-code-preview-container">
          {currentQrCode ? (
            <img
              src={currentQrCode}
              alt="活码二维码"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : (
            <div className="qr-code-empty-placeholder">
              暂无二维码
            </div>
          )}
          <div className="qr-code-error-placeholder" style={{ display: 'none' }}>
            二维码加载失败
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WeworkContactWay; 